/**
 * ClientOmnibox — combined search + tag filter input.
 *
 * Renders as a single token-input field: selected tags appear as removable
 * pills inside the box, free text becomes the `q` search. An autocomplete
 * dropdown (filtered from the full tag vocabulary) lets users add tag tokens.
 *
 * This component is stateless w.r.t. URL — the parent owns `searchInput`,
 * `selectedTags`, and the callbacks to update them.
 */

import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { Search, X } from "lucide-react";

interface TagOption {
  id: string;
  name: string;
  slug: string;
}

interface ClientOmniboxProps {
  /** Live search text (the typing buffer). */
  searchInput: string;
  /** Called on every keystroke in the free-text portion. */
  onSearchChange: (value: string) => void;
  /** Currently active tag slugs. */
  selectedTags: string[];
  /** Add a tag by slug. */
  onTagAdd: (slug: string) => void;
  /** Remove a tag by slug. */
  onTagRemove: (slug: string) => void;
  /** Clear the whole omnibox (search text + all tag filters). */
  onClear: () => void;
  /** Full tag vocabulary (all existing tags). */
  vocabulary: TagOption[];
  /** Placeholder when empty. */
  placeholder?: string;
}

export function ClientOmnibox({
  searchInput,
  onSearchChange,
  selectedTags,
  onTagAdd,
  onTagRemove,
  onClear,
  vocabulary,
  placeholder = "Search clients or filter by tag...",
}: ClientOmniboxProps) {
  const hasValue = searchInput.length > 0 || selectedTags.length > 0;
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Set of currently selected slugs for quick membership checks
  const selectedSet = useMemo(
    () => new Set(selectedTags),
    [selectedTags],
  );

  // Resolve selected slugs to full tag objects for pill display
  const selectedTagObjects = useMemo(() => {
    const bySlug = new Map(vocabulary.map((t) => [t.slug, t]));
    return selectedTags
      .map((slug) => bySlug.get(slug))
      .filter((t): t is TagOption => t != null);
  }, [selectedTags, vocabulary]);

  // Filter vocabulary to unselected tags matching the typed text
  const suggestions = useMemo(() => {
    const trimmed = searchInput.trim().toLowerCase();
    return vocabulary.filter(
      (t) =>
        !selectedSet.has(t.slug) &&
        (trimmed === "" || t.name.toLowerCase().includes(trimmed)),
    );
  }, [vocabulary, selectedSet, searchInput]);

  // Reset highlight when suggestions change
  useEffect(() => {
    setHighlightIdx(-1);
  }, [suggestions.length]);

  const selectTag = useCallback(
    (slug: string) => {
      onTagAdd(slug);
      onSearchChange(""); // clear the typed text after selecting a tag
      setOpen(false);
      inputRef.current?.focus();
    },
    [onTagAdd, onSearchChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown" && open && suggestions.length > 0) {
        e.preventDefault();
        setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1));
      } else if (e.key === "ArrowUp" && open && suggestions.length > 0) {
        e.preventDefault();
        setHighlightIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && open && highlightIdx >= 0 && highlightIdx < suggestions.length) {
        e.preventDefault();
        selectTag(suggestions[highlightIdx].slug);
      } else if (e.key === "Escape") {
        setOpen(false);
      } else if (
        e.key === "Backspace" &&
        searchInput === "" &&
        selectedTags.length > 0
      ) {
        // Remove the last tag token on backspace with empty input
        onTagRemove(selectedTags[selectedTags.length - 1]);
      }
    },
    [open, suggestions, highlightIdx, searchInput, selectedTags, selectTag, onTagRemove],
  );

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Decide whether to show the dropdown: only when input is focused AND
  // there's typed text that could match tags (avoids a giant dropdown on
  // bare focus).
  const showDropdown = open && searchInput.trim().length > 0 && suggestions.length > 0;

  return (
    <div ref={containerRef} className="relative w-full max-w-xl">
      {/* Token-input container */}
      <div
        className={`flex flex-wrap items-center gap-1.5 pl-8 py-1.5 rounded-md bg-paper-hi hair border text-[13px] transition min-h-11 md:min-h-[38px] ${
          hasValue ? "pr-9" : "pr-3"
        } ${open ? "border-tide" : ""}`}
        onClick={() => inputRef.current?.focus()}
      >
        {/* Search icon — positioned absolutely so it stays left-aligned */}
        <Search
          size={13}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-5"
          strokeWidth={2}
        />

        {/* Selected tag pills */}
        {selectedTagObjects.map((tag) => (
          <span
            key={tag.slug}
            className="inline-flex items-center gap-1 bg-tide text-paper-hi rounded px-2 py-0.5 text-[12px] font-medium flex-shrink-0"
          >
            {tag.name}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onTagRemove(tag.slug);
                inputRef.current?.focus();
              }}
              className="hover:text-paper-hi/70 cursor-pointer -mr-0.5"
              aria-label={`Remove ${tag.name} filter`}
            >
              <X size={10} strokeWidth={2.5} />
            </button>
          </span>
        ))}

        {/* Text input */}
        <input
          ref={inputRef}
          type="text"
          value={searchInput}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (searchInput.trim().length > 0) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          aria-label="Search clients by name, email, or tag"
          placeholder={selectedTags.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-ink placeholder:text-ink-4 text-[13px] py-0.5"
        />

        {/* Clear-all button — wipes search text + tag filters */}
        {hasValue && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
              inputRef.current?.focus();
            }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-5 hover:text-ink-3 cursor-pointer"
            aria-label="Clear search and tag filters"
          >
            <X size={14} strokeWidth={2} />
          </button>
        )}
      </div>

      {/* Autocomplete dropdown */}
      {showDropdown && (
        <div className="absolute z-20 mt-1 w-full bg-paper-hi rounded-md shadow-lift hair border max-h-48 overflow-y-auto">
          {suggestions.map((tag, idx) => (
            <button
              key={tag.slug}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault(); // prevent input blur
                selectTag(tag.slug);
              }}
              onMouseEnter={() => setHighlightIdx(idx)}
              className={`w-full text-left px-3 py-2 text-[12.5px] flex items-center gap-2 ${
                idx === highlightIdx
                  ? "bg-tide-ss text-ink"
                  : "text-ink-2 hover:bg-canvas"
              }`}
            >
              <span>{tag.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
